import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import { cn } from '../../cn';

const Pagination = ({
  currentPage,
  totalPages,
  onPageChange,
  itemsPerPage = 10,
  totalCount = 0,
  onItemsPerPageChange,
  className = ''
}) => {
  const normalizedTotalPages = Math.max(0, Number(totalPages) || 0);
  const normalizedCurrentPage = normalizedTotalPages > 0
    ? Math.min(Math.max(1, Number(currentPage) || 1), normalizedTotalPages)
    : 1;

  const pageNumbers = useMemo(() => {
    if (normalizedTotalPages <= 1) return [1];
    if (normalizedTotalPages <= 7) {
      return Array.from({ length: normalizedTotalPages }, (_, index) => index + 1);
    }

    const pages = [1];
    const start = Math.max(2, normalizedCurrentPage - 1);
    const end = Math.min(normalizedTotalPages - 1, normalizedCurrentPage + 1);

    if (start > 2) pages.push('...');
    for (let page = start; page <= end; page += 1) pages.push(page);
    if (end < normalizedTotalPages - 1) pages.push('...');

    pages.push(normalizedTotalPages);
    return pages;
  }, [normalizedCurrentPage, normalizedTotalPages]);

  const rowsOptions = useMemo(() => {
    const base = [10, 25, 50, 100, 250, 500];
    return [...new Set([...base, Number(itemsPerPage) || 10])].sort((a, b) => a - b);
  }, [itemsPerPage]);

  const from = totalCount === 0 ? 0 : (normalizedCurrentPage - 1) * itemsPerPage + 1;
  const to = totalCount === 0 ? 0 : Math.min(normalizedCurrentPage * itemsPerPage, totalCount);

  const handlePageClick = (page) => {
    if (typeof page === 'number' && page >= 1 && page <= normalizedTotalPages) {
      onPageChange?.(page);
    }
  };

  const navButtonClass = (disabled) => cn(
    'relative inline-flex items-center border border-gray-300 bg-white text-sm font-medium',
    disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
  );

  return (
    <nav className={cn('mt-4 border-t border-neutral-200 pt-3 pb-1 flex items-center justify-between', className)} aria-label="Pagination">
      <div className="flex-1 flex justify-between sm:hidden">
        <button type="button" onClick={() => handlePageClick(normalizedCurrentPage - 1)} disabled={normalizedCurrentPage === 1} className={cn(navButtonClass(normalizedCurrentPage === 1), 'px-4 py-2 rounded-md')}>
          Previous
        </button>
        <button type="button" onClick={() => handlePageClick(normalizedCurrentPage + 1)} disabled={normalizedCurrentPage === normalizedTotalPages || normalizedTotalPages === 0} className={cn(navButtonClass(normalizedCurrentPage === normalizedTotalPages || normalizedTotalPages === 0), 'ml-3 px-4 py-2 rounded-md')}>
          Next
        </button>
      </div>

      <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
        <p className="text-sm text-gray-700">
          Showing <span className="font-medium">{from}</span> to <span className="font-medium">{to}</span> of <span className="font-medium">{totalCount}</span> results
        </p>

        <div className="flex items-center space-x-4">
          {onItemsPerPageChange && (
            <select
              id="itemsPerPage"
              value={itemsPerPage}
              onChange={(event) => onItemsPerPageChange(Number(event.target.value))}
              className="border border-gray-300 bg-white ring-0 shadow-sm rounded-md px-3 py-3 text-sm focus:ring-primary-500 focus:border-primary-500"
            >
              {rowsOptions.map((option) => (
                <option key={option} value={option}>{option} rows</option>
              ))}
            </select>
          )}

          <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
            <button type="button" onClick={() => handlePageClick(normalizedCurrentPage - 1)} disabled={normalizedCurrentPage === 1} className={cn(navButtonClass(normalizedCurrentPage === 1), 'px-2 py-2 rounded-l-md')}>
              <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
              <span className="sr-only">Previous</span>
            </button>

            {pageNumbers.map((pageNumber, index) => (
              pageNumber === '...' ? (
                <span key={`ellipsis-${index}`} className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">...</span>
              ) : (
                <button
                  key={pageNumber}
                  type="button"
                  aria-current={normalizedCurrentPage === pageNumber ? 'page' : undefined}
                  onClick={() => handlePageClick(pageNumber)}
                  className={cn(
                    'relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium',
                    normalizedCurrentPage === pageNumber ? 'z-10 bg-primary-50 text-primary-600' : 'text-gray-500 hover:bg-gray-50'
                  )}
                >
                  {pageNumber}
                </button>
              )
            ))}

            <button type="button" onClick={() => handlePageClick(normalizedCurrentPage + 1)} disabled={normalizedCurrentPage === normalizedTotalPages || normalizedTotalPages === 0} className={cn(navButtonClass(normalizedCurrentPage === normalizedTotalPages || normalizedTotalPages === 0), 'px-2 py-2 rounded-r-md')}>
              <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
              <span className="sr-only">Next</span>
            </button>
          </nav>
        </div>
      </div>
    </nav>
  );
};

Pagination.displayName = 'Pagination';

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  totalPages: PropTypes.number.isRequired,
  onPageChange: PropTypes.func,
  itemsPerPage: PropTypes.number,
  totalCount: PropTypes.number,
  onItemsPerPageChange: PropTypes.func,
  className: PropTypes.string
};

export default Pagination;
